/** XeAgents.ai**/
import { useTranslation } from "react-i18next";
import { useBranding } from "@/components/BrandingProvider";
import { useSeoSettings } from "@/hooks/useSeoSettings";

const DEFAULT_KEYWORDS = [
  "AI voice agents for healthcare",
  "healthcare call automation",
  "appointment booking AI",
  "appointment reminder calls",
  "patient enquiries AI",
  "AI phone agents",
  "clinic call automation",
  "voice AI",
  "inbound and outbound calling",
  "telephony integration",
];

const DEFAULT_TITLE = "AI Voice Agents for Healthcare Operations";

const DEFAULT_DESCRIPTION =
  "Automate patient calls, appointments, reminders and routine enquiries with AI voice agents that complete the task, record the outcome and hand off to staff when needed.";

/** CSS hooks the speakable schema points voice assistants at. */
const SPEAKABLE_SELECTORS = [".hero-description", ".key-features", ".faq-answer"];

/**
 * Resolves the props for the landing page's <SEOHead>: admin-configured SEO
 * settings first, then tenant branding, then our shipped defaults.
 */
export function useLandingSeo() {
  const { branding } = useBranding();
  const { data: seoSettings } = useSeoSettings();
  const { t, i18n } = useTranslation("landing");

  // Admin SEO settings and the branding tagline are written in English, so a
  // visitor reading the page in another language gets the translated defaults.
  const isEnglish = (i18n.resolvedLanguage ?? "en") === "en";
  const title = isEnglish ? seoSettings?.defaultTitle || DEFAULT_TITLE : t("seo.title");
  const description = isEnglish
    ? seoSettings?.defaultDescription || branding.app_tagline || DEFAULT_DESCRIPTION
    : t("seo.description");

  // AEO schemas are newer than the typed settings shape, hence the cast.
  const extendedSettings = seoSettings as Record<string, any> | undefined;

  return {
    title,
    description,
    canonicalUrl: seoSettings?.canonicalBaseUrl || undefined,
    ogImage: seoSettings?.defaultOgImage || undefined,
    ogSiteName: branding.app_name,
    keywords:
      seoSettings?.defaultKeywords && seoSettings.defaultKeywords.length > 0
        ? seoSettings.defaultKeywords
        : DEFAULT_KEYWORDS,
    twitterSite: seoSettings?.twitterHandle || undefined,
    twitterCreator: seoSettings?.twitterHandle || undefined,
    googleVerification: seoSettings?.googleVerification || undefined,
    bingVerification: seoSettings?.bingVerification || undefined,
    facebookAppId: seoSettings?.facebookAppId || undefined,
    structuredDataOrg: seoSettings?.structuredDataOrg,
    structuredDataFaq: seoSettings?.structuredDataFaq,
    structuredDataProduct: seoSettings?.structuredDataProduct,
    structuredDataHowTo: extendedSettings?.structuredDataHowTo || undefined,
    structuredDataSpeakable: extendedSettings?.structuredDataSpeakable || {
      cssSelector: SPEAKABLE_SELECTORS,
    },
    structuredDataWebPage: extendedSettings?.structuredDataWebPage || {
      name: title,
      description,
    },
  };
}
